import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '@/components/DataTable';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { EditOnly } from '@/components/EditOnly';
import { deletePlayable, getPlayables, updatePlayable, uploadPlayable, type Playable } from '@/api/playables';
import { formatDateTime } from '@/lib/datetime';

function statusBadge(status: string) {
  const cls = status === 'active' ? 'bg-success-soft text-success' : 'bg-surface-muted text-content-muted';
  return <span className={`px-2 py-0.5 rounded text-xs ${cls}`}>{status}</span>;
}

function formatBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export function Playables() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [indexPath, setIndexPath] = useState('');
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: items = [], isLoading } = useQuery({ queryKey: ['playables'], queryFn: getPlayables });

  const uploadMut = useMutation({
    mutationFn: ({ file, name, indexPath }: { file: File; name: string; indexPath?: string }) =>
      uploadPlayable(file, name, indexPath),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['playables'] });
      setOpen(false); setName(''); setIndexPath(''); setError('');
      if (fileRef.current) fileRef.current.value = '';
    },
    onError: (e: Error) => setError(e.message),
  });

  const renameMut = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => updatePlayable(id, { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['playables'] }),
  });

  const toggleStatusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => updatePlayable(id, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['playables'] }),
  });

  const deleteMut = useMutation({
    mutationFn: deletePlayable,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['playables'] }),
    onError: (e: Error) => alert(e.message),
  });

  const onSubmit = () => {
    setError('');
    const file = fileRef.current?.files?.[0];
    if (!file) { setError('Choose a .zip file'); return; }
    if (!name.trim()) { setError('Name is required'); return; }
    uploadMut.mutate({ file, name: name.trim(), indexPath: indexPath.trim() || undefined });
  };

  const columns = [
    { key: 'name', label: 'Name', render: (p: Playable) => (
      <div>
        <div className="font-medium">{p.name}</div>
        <div className="text-xs text-content-subtle font-mono">{p.id}</div>
      </div>
    )},
    { key: 'index_path', label: 'Index', render: (p: Playable) => <span className="text-xs font-mono">{p.index_path}</span> },
    { key: 'size_bytes', label: 'Size', render: (p: Playable) => <span className="text-xs">{formatBytes(p.size_bytes)}</span> },
    { key: 'status', label: 'Status', render: (p: Playable) => statusBadge(p.status) },
    { key: 'created_at', label: 'Created', render: (p: Playable) => <span className="text-xs text-content-subtle">{formatDateTime(p.created_at)}</span> },
    { key: 'actions', label: '', render: (p: Playable) => (
      <EditOnly>
        <div className="flex gap-3 text-xs">
          <button onClick={(e) => { e.stopPropagation(); const n = prompt('New name', p.name); if (n && n !== p.name) renameMut.mutate({ id: p.id, name: n }); }} className="text-primary hover:underline">Rename</button>
          <button onClick={(e) => { e.stopPropagation(); toggleStatusMut.mutate({ id: p.id, status: p.status === 'active' ? 'archived' : 'active' }); }} className="text-content-muted hover:underline">
            {p.status === 'active' ? 'Archive' : 'Activate'}
          </button>
          <button onClick={(e) => { e.stopPropagation(); if (confirm(`Delete "${p.name}"? This removes all uploaded files.`)) deleteMut.mutate(p.id); }} className="text-danger hover:underline">Delete</button>
        </div>
      </EditOnly>
    )},
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Playables</h1>
          <p className="text-sm text-content-subtle mt-1">
            Upload interactive HTML5 ads (zip with index.html). Use{' '}
            <code className="text-xs bg-surface-muted px-1 rounded">{'{{OFFER_URL}}'}</code> inside the HTML
            for the campaign link macro.
          </p>
        </div>
        <EditOnly><Button onClick={() => { setName(''); setIndexPath(''); setError(''); setOpen(true); }}>Upload playable</Button></EditOnly>
      </div>

      <DataTable columns={columns} data={items} loading={isLoading} keyExtractor={(p) => p.id} />

      <Modal isOpen={open} onClose={() => setOpen(false)} title="Upload playable" size="md">
        <div className="space-y-4">
          {error && <div className="p-3 bg-danger-soft text-sm text-danger rounded">{error}</div>}
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="My playable v1"
              className="w-full px-3 py-2 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Index path <span className="text-content-subtle font-normal">(optional, default index.html)</span></label>
            <input
              type="text" value={indexPath} onChange={(e) => setIndexPath(e.target.value)}
              placeholder="index.html"
              className="w-full px-3 py-2 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Zip file</label>
            <input
              ref={fileRef} type="file" accept=".zip,application/zip"
              className="block w-full text-sm text-content-muted file:mr-3 file:py-2 file:px-4 file:rounded file:border-0 file:bg-primary/10 file:text-primary hover:file:bg-primary/10"
            />
            <p className="mt-1 text-xs text-content-subtle">Max 25 MB compressed / 60 MB unzipped.</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={onSubmit} loading={uploadMut.isPending}>Upload</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
